import { Controller } from "@hotwired/stimulus";

const VIEWBOX_SIZE = 760;
const MAX_DPR = 2;
const TWO_PI = Math.PI * 2;

const NETWORK_LAYERS = [
  {
    phase: 0.8,
    lineAlpha: 0.09,
    accentLineAlpha: 0.15,
    points: [
      point("a1", 464, 96, { pulse: true }),
      point("a2", 578, 166, { accent: true, pulse: true }),
      point("a3", 534, 280, { pulse: true }),
      point("a4", 648, 358, { accent: true, pulse: true }),
      point("a5", 594, 520, { accent: true }),
      point("a6", 706, 598),
      point("a7", 512, 148),
      point("a8", 432, 246),
      point("a9", 486, 388, { accent: true }),
      point("a10", 410, 494),
      point("a11", 448, 648, { pulse: true }),
      point("a12", 606, 210),
      point("a13", 694, 252),
      point("a14", 646, 382),
      point("a15", 720, 470),
      point("a16", 430, 286),
      point("a17", 578, 338),
      point("a18", 520, 458),
      point("a19", 634, 550),
    ],
    edges: [
      edge("a1", "a2"),
      edge("a2", "a3"),
      edge("a3", "a4"),
      edge("a4", "a5"),
      edge("a5", "a6"),
      edge("a7", "a8", true),
      edge("a8", "a9", true),
      edge("a9", "a10", true),
      edge("a10", "a11", true),
      edge("a12", "a13"),
      edge("a13", "a14"),
      edge("a14", "a15"),
      edge("a16", "a17"),
      edge("a17", "a18"),
      edge("a18", "a19"),
    ],
  },
  {
    phase: 1.9,
    lineAlpha: 0.11,
    accentLineAlpha: 0.2,
    points: [
      point("b1", 540, 78, { accent: true, pulse: true }),
      point("b2", 664, 168, { pulse: true }),
      point("b3", 612, 298, { pulse: true }),
      point("b4", 708, 388, { accent: true, pulse: true }),
      point("b5", 676, 552),
      point("b6", 560, 228),
      point("b7", 478, 324),
      point("b8", 540, 458, { accent: true }),
      point("b9", 486, 610, { pulse: true }),
      point("b10", 742, 236),
      point("b11", 712, 320),
      point("b12", 622, 492, { pulse: true }),
      point("b13", 730, 620, { accent: true, pulse: true }),
    ],
    edges: [
      edge("b1", "b2", true),
      edge("b2", "b3", true),
      edge("b3", "b4", true),
      edge("b4", "b5", true),
      edge("b6", "b7"),
      edge("b7", "b8"),
      edge("b8", "b9"),
      edge("b2", "b10"),
      edge("b10", "b11"),
      edge("b3", "b4"),
      edge("b4", "b12"),
      edge("b12", "b13"),
    ],
  },
];

export default class extends Controller {
  static targets = ["canvas"];

  connect() {
    this.hostElement = this.element;
    this.canvasElement =
      this.hostElement.querySelector('[data-hero-network-target="canvas"]');
    if (!this.canvasElement) return;

    this.context = this.canvasElement.getContext("2d");
    if (!this.context) return;

    this.frameId = null;
    this.width = 0;
    this.height = 0;
    this.scaleX = 1;
    this.scaleY = 1;
    this.isVisible = true;

    this.drawFrame = this.drawFrame.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleReducedMotionChange = this.handleReducedMotionChange.bind(this);

    this.motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.observeReducedMotion();

    this.resizeObserver = new ResizeObserver(this.handleResize);
    this.resizeObserver.observe(this.hostElement);

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        this.isVisible = entries[0]?.isIntersecting ?? true;
        if (this.isVisible) {
          this.start();
        } else {
          this.stop();
        }
      },
      { threshold: 0.05 },
    );
    this.intersectionObserver.observe(this.hostElement);

    window.addEventListener("resize", this.handleResize);
    this.handleResize();
  }

  disconnect() {
    this.stop();

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }

    window.removeEventListener("resize", this.handleResize);
    this.unobserveReducedMotion();
  }

  observeReducedMotion() {
    if (!this.motionQuery) return;

    if (this.motionQuery.addEventListener) {
      this.motionQuery.addEventListener("change", this.handleReducedMotionChange);
    } else {
      this.motionQuery.addListener(this.handleReducedMotionChange);
    }
  }

  unobserveReducedMotion() {
    if (!this.motionQuery) return;

    if (this.motionQuery.removeEventListener) {
      this.motionQuery.removeEventListener("change", this.handleReducedMotionChange);
    } else {
      this.motionQuery.removeListener(this.handleReducedMotionChange);
    }
  }

  handleReducedMotionChange() {
    if (this.prefersReducedMotion()) {
      this.stop();
      this.hostElement?.classList.remove("is-live");
      return;
    }

    this.handleResize();
    this.start();
  }

  prefersReducedMotion() {
    return this.motionQuery?.matches;
  }

  handleResize() {
    if (!this.context) return;

    const width = this.hostElement?.clientWidth || 0;
    const height = this.hostElement?.clientHeight || 0;

    if (!width || !height || this.prefersReducedMotion()) {
      this.width = 0;
      this.height = 0;
      this.hostElement?.classList.remove("is-live");
      this.stop();
      return;
    }

    const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);

    this.canvasElement.width = Math.round(width * dpr);
    this.canvasElement.height = Math.round(height * dpr);
    this.canvasElement.style.width = `${width}px`;
    this.canvasElement.style.height = `${height}px`;

    this.width = width;
    this.height = height;
    this.scaleX = width / VIEWBOX_SIZE;
    this.scaleY = height / VIEWBOX_SIZE;
    this.context.setTransform(dpr, 0, 0, dpr, 0, 0);

    this.hostElement.classList.add("is-live");
    this.draw(performance.now());
    this.start();
  }

  start() {
    if (
      this.frameId ||
      !this.context ||
      !this.width ||
      !this.height ||
      !this.isVisible ||
      this.prefersReducedMotion()
    ) {
      return;
    }

    this.startTime ||= performance.now();
    this.frameId = requestAnimationFrame(this.drawFrame);
  }

  stop() {
    if (this.frameId) {
      cancelAnimationFrame(this.frameId);
      this.frameId = null;
    }
  }

  drawFrame(now) {
    this.frameId = null;

    if (
      !this.context ||
      !this.width ||
      !this.height ||
      !this.isVisible ||
      this.prefersReducedMotion()
    ) {
      return;
    }

    this.draw(now);
    this.frameId = requestAnimationFrame(this.drawFrame);
  }

  draw(now) {
    const elapsed = (now - (this.startTime || now)) / 1000;

    this.context.clearRect(0, 0, this.width, this.height);

    NETWORK_LAYERS.forEach((layer, layerIndex) => {
      const positions = this.resolvePositions(layer, elapsed, layerIndex);
      this.drawEdges(layer, positions);
      this.drawJoints(layer, positions, elapsed);
    });
  }

  resolvePositions(layer, elapsed, layerIndex) {
    const positions = new Map();
    const layerDriftX =
      Math.sin(elapsed * (0.18 + layerIndex * 0.03) + layer.phase) *
      4.5 *
      this.scaleX;
    const layerDriftY =
      Math.cos(elapsed * (0.16 + layerIndex * 0.025) + layer.phase * 1.2) *
      3.75 *
      this.scaleY;

    layer.points.forEach((point, index) => {
      const amplitude = point.pulse ? 7.5 : point.accent ? 6.2 : 4.8;
      const ampX = amplitude * this.scaleX;
      const ampY = amplitude * 0.9 * this.scaleY;
      const phase = point.seed + layer.phase + index * 0.19;
      const x =
        point.x * this.scaleX +
        Math.sin(elapsed * (0.42 + point.seed * 0.015) + phase) * ampX +
        Math.cos(elapsed * (0.21 + point.seed * 0.01) + phase * 1.7) *
          ampX *
          0.42 +
        layerDriftX;
      const y =
        point.y * this.scaleY +
        Math.cos(elapsed * (0.37 + point.seed * 0.012) + phase * 1.4) * ampY +
        Math.sin(elapsed * (0.19 + point.seed * 0.008) + phase * 0.85) *
          ampY *
          0.36 +
        layerDriftY;

      positions.set(point.id, { x, y });
    });

    return positions;
  }

  drawEdges(layer, positions) {
    const ctx = this.context;
    const averageScale = (this.scaleX + this.scaleY) / 2;

    ctx.save();
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    layer.edges.forEach((connection) => {
      const start = positions.get(connection.from);
      const finish = positions.get(connection.to);
      if (!start || !finish) return;

      ctx.beginPath();
      ctx.moveTo(start.x, start.y);
      ctx.lineTo(finish.x, finish.y);
      ctx.strokeStyle = connection.accent
        ? rgba(176, 149, 255, layer.accentLineAlpha)
        : rgba(255, 255, 255, layer.lineAlpha);
      ctx.lineWidth = connection.accent
        ? Math.max(0.95, 1.3 * averageScale)
        : Math.max(0.8, 1.05 * averageScale);
      ctx.stroke();
    });

    ctx.restore();
  }

  drawJoints(layer, positions, elapsed) {
    const ctx = this.context;
    const averageScale = (this.scaleX + this.scaleY) / 2;

    layer.points.forEach((point) => {
      const position = positions.get(point.id);
      if (!position) return;

      const pulse =
        point.pulse
          ? (Math.sin(elapsed * (1.25 + point.seed * 0.03) + point.seed) + 1) /
            2
          : 0;
      const glowRadius =
        (point.accent ? 8.5 : 6.2) * averageScale + pulse * 1.8;
      const jointRadius = (point.accent ? 2.8 : 2.25) * averageScale;
      const nodeRadius = point.pulse
        ? (point.accent ? 3.7 : 3.15) * averageScale + pulse * 1.15
        : 0;

      ctx.save();
      ctx.shadowBlur = point.accent ? 18 : 12;
      ctx.shadowColor = point.accent
        ? rgba(176, 149, 255, 0.3)
        : rgba(255, 255, 255, 0.18);
      ctx.fillStyle = point.accent
        ? rgba(176, 149, 255, 0.16)
        : rgba(255, 255, 255, 0.08);
      circle(ctx, position.x, position.y, glowRadius);
      ctx.restore();

      ctx.fillStyle = point.accent
        ? rgba(188, 166, 255, 0.8)
        : rgba(255, 255, 255, 0.28);
      circle(ctx, position.x, position.y, jointRadius);

      if (point.pulse) {
        ctx.save();
        ctx.shadowBlur = point.accent ? 16 : 10;
        ctx.shadowColor = point.accent
          ? rgba(188, 166, 255, 0.42)
          : rgba(255, 255, 255, 0.24);
        ctx.fillStyle = point.accent
          ? rgba(188, 166, 255, 0.68)
          : rgba(255, 255, 255, 0.42);
        circle(ctx, position.x, position.y, nodeRadius);
        ctx.restore();
      }
    });
  }
}

function point(id, x, y, options = {}) {
  return {
    id,
    x,
    y,
    accent: false,
    pulse: false,
    seed: x * 0.013 + y * 0.017,
    ...options,
  };
}

function edge(from, to, accent = false) {
  return { from, to, accent };
}

function circle(ctx, x, y, radius) {
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, TWO_PI);
  ctx.fill();
}

function rgba(red, green, blue, alpha) {
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}
